// pages/NotePage.js
import React, { useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useNote } from "../hooks/useNote";
import MapboxMap from "../components/map/MapboxMap";
import NoteModal from "../components/notes/NoteModal";
import NoteCard from "../components/notes/NoteCard";
import PhotoLightbox from "../components/PhotoLightbox";
import Spinner from "../ui/Spinner";
import ErrorBanner from "../ui/ErrorBanner";

/**
 * Page for viewing a single note via shared link or notification.
 * Route: /n/:noteId
 *
 * When the note has coordinates, renders the same MapboxMap + NoteModal
 * combo used on the Home page. The modal starts open so the user sees
 * the note immediately and can close it to reveal the map with the pin.
 * Tapping the pin reopens the modal.
 *
 * Falls back to a plain card layout when coordinates are unavailable.
 */
export default function NotePage() {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: note, loading, error, refetch } = useNote(noteId);

  // Modal starts open so user sees the note content immediately
  const [modalOpen, setModalOpen] = useState(true);

  // URL params for deep-linking into comments
  const openComments = searchParams.get("openComments") === "true";
  const highlightCommentId = searchParams.get("highlightComment");

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleMarkerClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleNoteDeleted = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const handleNoteUpdated = useCallback(() => {
    refetch();
  }, [refetch]);

  // ── Determine layout mode ────────────────────────────────────────────

  const hasCoords =
    note &&
    note.latitude != null &&
    note.longitude != null &&
    !isNaN(note.latitude) &&
    !isNaN(note.longitude);

  // ── Map layout (note has coordinates) ────────────────────────────────

  if (hasCoords || loading) {
    return (
      <div className="flex-1 bg-surface flex items-center justify-center text-text-muted">
        {/* Loading state (before note data arrives) */}
        {loading && (
          <div className="flex items-center justify-center">
            <Spinner size={26} className="text-text-muted/60" />
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="p-4">
            <ErrorBanner
              message={error}
              onRetry={refetch}
              dismissible={false}
            />
          </div>
        )}

        {/* MapboxMap — same component as Home page, with one note */}
        {hasCoords && (
          <>
            <MapboxMap
              notes={[note]}
              selectedNote={null}
              initialViewState={{
                longitude: note.longitude,
                latitude: note.latitude,
                zoom: 14,
              }}
              onMarkerClick={handleMarkerClick}
              showRadiusCircle={false}
              settingsPopover={null}
            />

            <NoteModal
              open={modalOpen}
              note={note}
              loading={false}
              onClose={handleModalClose}
              onNoteDeleted={handleNoteDeleted}
              onNoteUpdated={handleNoteUpdated}
              initialCommentsOpen={openComments}
              highlightCommentId={highlightCommentId}
            />
          </>
        )}
      </div>
    );
  }

  // ── Fallback layout (no coordinates) ─────────────────────────────────

  return (
    <FallbackLayout
      note={note}
      loading={loading}
      error={error}
      refetch={refetch}
      openComments={openComments}
      highlightCommentId={highlightCommentId}
      onNoteDeleted={handleNoteDeleted}
    />
  );
}

/**
 * Plain card layout used when the note has no coordinates.
 * Matches the original NotePage structure.
 */
function FallbackLayout({
  note,
  loading,
  error,
  refetch,
  openComments,
  highlightCommentId,
  onNoteDeleted,
}) {
  const [isPhotoView, setIsPhotoView] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const images = Array.isArray(note?.images) ? note.images : [];
  const hasImages = images.length > 0;

  const handleRequestPhotoView = useCallback(
    (i) => {
      if (!hasImages) return;
      let next = Number.isInteger(i) ? i : 0;
      if (next < 0) next = 0;
      if (next >= images.length) next = images.length - 1;
      setPhotoIndex(next);
      setIsPhotoView(true);
    },
    [hasImages, images.length],
  );

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 md:px-10 py-6 bg-surface pretty-scroll">
        <div className="max-w-2xl mx-auto">
          {loading && (
            <div className="flex items-center justify-center py-10 text-text-muted">
              <Spinner size={26} className="text-text-muted/60" />
            </div>
          )}

          {error && (
            <div className="flex justify-center py-10">
              <ErrorBanner
                message={error}
                onRetry={refetch}
                dismissible={false}
              />
            </div>
          )}

          {!loading && !error && note && (
            <div className="rounded-2xl shadow-2xl bg-surface-elevated border border-border">
              <NoteCard
                note={note}
                showClose={false}
                onRequestPhotoView={handleRequestPhotoView}
                onNoteUpdated={refetch}
                onNoteDeleted={onNoteDeleted}
                initialCommentsOpen={openComments}
                highlightCommentId={highlightCommentId}
              />
            </div>
          )}
        </div>
      </div>

      {isPhotoView && hasImages && (
        <PhotoLightbox
          open={isPhotoView}
          images={images}
          index={photoIndex}
          onClose={() => setIsPhotoView(false)}
          onIndexChange={setPhotoIndex}
        />
      )}
    </>
  );
}
